import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import client from '@/store/client';
import navbarStore from '@/store/navbar';
import chatStoreManager from '@/store/chat/manager';
import { getChatContext } from '@/services/chat';
import { HelperCore } from '@/common/helper';
import { LocalModelConf } from '@/common/conf/localModelConf';
import globalStore from '@/store/global';

const maxCount = 4096 * 1.5;

function getLocalModelInfo() {
  return new Promise((resolve) => {
    client.run('GetInfo', (data) => {
      // console.log('GetInfo', data);
      resolve(data || null)
    }, 'localModelStatus')
  })
}

class LocalRobotStore {
  localModels = [];
  count = 0;
  timer = null;
  isThink = false;
  isDone = true;
  conversationId = '';
  thinkStartTime = 0;
  dataset = null;
  lastDocs = [];
  onopen = () => {};
  onmessage = () => {};
  onclose = () => {};
  onerror = () => {};
  initReady = false;
  initReadyCallback = [];

  // 服务端存储用，描述文档和msg的对应关系msgId，文档模式使用
  msgId = null;

  // 联网搜索素材,生成上下文和历史记录用的
  referSearch = null;
  searchKeyword = '';
  searchTotal = 0

  // 用户选择的附件，生成上下文和历史记录用的
  file = null;

  constructor() {
    makeAutoObservable(this);

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === 'visible') {
        this.start();
      }
    }, false);

    if (!HelperCore.IS_PROD) {
      window._localRobot = this;
    }
  }

  start() {
    client.run('GetOllamaInfo', res => {
      if (res?.hasollama) {
        // 获取模型列表
        client.run("OllamaQuery", (data, params) => {
          // console.log('api/tags', data, params)
        }, {
          api: "/api/tags",
          method: 1,
          callback: "_wsClientCbTags"
        });
      }
    })
  }

  query(chatModel, msg) {
    console.log('OllamaQuery', chatModel, msg);
    
    this.count = 0;
    this.isThink = false;
    //2分钟没收到，就终止
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      if (this.count === 0) {
        this.stop();
      }
    }, 2 * 60 * 1000);

    client.run("OllamaQuery", (data, params) => {
      // console.log(data, params)
    }, {
      api: "/api/chat",
      method: 0,
      timeout: 0, // 不限时间
      param: JSON.stringify({
        model: chatModel,
        stream: true,
        messages: msg,
      }),
      stream: true,
      callback: globalStore.isKownledgeChat() ? "_wsClientMsg2" : "_wsClientMsg"
    })
  }

  getLocalContext(conversation_id, prompt) {
    const chatStore = chatStoreManager.get(this.conversationId, 'local');
    const { messageList } = chatStore;
    const context = [];
    if (conversation_id) {
      let remainCount = maxCount - prompt.length;
      messageList.slice(-10).forEach(item => {
        if (!item.conversation_id || item.conversation_id === this.conversationId && item?.status !== -1) {
          const resultCount = item.result.length;
          if (remainCount >= resultCount) {
            context.push({
              type: 0,
              prompt: item.prompt,
              result: item.result,
            });
            remainCount -= resultCount;
          } else if (remainCount > 0) {
            context.push({
              type: 0,
              prompt,
              result: item.result.substr(0, remainCount),
            });
            remainCount -= resultCount;
          }
        }
      })
    }
    if (context.length === 0) {
      context.push({
        type: 1,
        // eslint 检查忽略
        // eslint-disable-next-line
        prompt: params.prompt,
      });
    }
    const messages = [];
    context.forEach(item => {
      messages.push({
        role: 'user',
        content: item.prompt,
      })
      if (item.type === 0 && item.result) {
        messages.push({
          role: 'assistant',
          content: item.result,
        })
      }
    });
    return messages;
  }

  async fetch(params, options) {
    this.onopen = options.onopen;
    this.onmessage = options.onmessage;
    this.onclose = options.onclose;
    this.onerror = options.onerror;
    this.isDone = false;

    const { conversation_id, role, annex_msg_id, dataset, prompt, agent_id, file, is_so, ks_id } = params;
    const chatModel = navbarStore.chatModels[role]?.apply_model;

    this.conversationId = conversation_id ? conversation_id : uuidv4().replace(/-/g, '');
    if (dataset && !this.dataset) {
      this.dataset = dataset;
    }

    this.msgId = `${+new Date}`;

    if(this.conversationId){
      options.onmessage({
        id: 0,
        event : 100,
        data : `CONVERSATIONID####${this.conversationId}`,
        retry: '',
      });
    }

    // 从本地获取对话上下文
    // const messages = this.getLocalContext(conversation_id);

    // 从服务端获取上下文
    const res = await getChatContext({
      conversation_id: this.conversationId,
      annex_msg_id,
      dataset: dataset || this.dataset,
      prompt,
      max_total: maxCount,
      agent_id,
      file,
      role_biz: file && file.length ? 'doc' : 'common',
      msg_id: this.msgId,
      model_id: role,
      is_so,
      ks_id,
      model_type: 1, // 1-本地模型
    });
    if (!res) {
      this.isDone = true;
      this.onclose();
      return;
    }

    this.lastDocs = res?.rag?.docs || []

    this.referSearch = res?.refer_search
    this.searchKeyword = res?.search_keyword;
    this.searchTotal = res?.search_total;

    this.file = file;
    
    this.query(chatModel, res?.messages);
  }

  getRobotByModelName(modelName) {
    for (const o in LocalModelConf) {
      if (LocalModelConf[o].model.toLowerCase() === modelName.toLowerCase()) {
        LocalModelConf[o].id = o;
        return LocalModelConf[o];
      }
    }
    return null;
  }

  updateModels(data, localModelMap) {
    let models = [];
    try {
      const json = JSON.parse(data);
      if (json?.models) {
        models = json.models;
      }
    } catch(e) {}
    models = models.filter(item => this.getRobotByModelName(item.model)).map(item => {
      let {
        model,
        name,
      } = item;
      model = model.toLowerCase();
      name = name.toLowerCase();
      const {
        id,
        icon,
        description,
        home_recommend,
      } = this.getRobotByModelName(model) || {};
      return {
        localModel: true,
        "apply_model": model,
        "chat_type": 101, // 本地模型
        "conversation_type": 0,
        "description": description,
        "dl_icon": "",
        "greeting": "", //"更擅长代码编程、数学计算和逻辑推理，协助更多「理科生」解决问题。",
        "home_recommend": home_recommend,
        "icon": icon,
        "id": id,
        "input_length": maxCount,
        "operating_angle": "",
        "saylist_detail": [],
        "sort2": 80001,
        "sort3": 0,
        "tag": "",
        "title": name,
        "tts_ct": "",
        is_preset: false,
      }
    })
    
    let newModels = models
    if (localModelMap) {
      newModels = models.filter(item => {
        return Object.entries(localModelMap).some(([key, value]) => {
          return key.toLowerCase() === item.apply_model && value;
        });
      });
    }
    this.localModels = newModels;
    navbarStore.setChatModels(newModels);
  }

  message(data) {
    clearTimeout(this.timer);
    let json = null;
    try {
      json = JSON.parse(data);
    } catch(err) {
      this.onerror();
      return;
    }
    if (json?.message) {
      this.count++;
      if (this.count === 1) {
        // const msg1 = {
        //   id: this.count,
        //   event: 100,
        //   retry: '',
        //   data: `CONVERSATIONID####${this.conversationId}`,
        // }
        // this.onmessage?.(msg1);

        this.count++;
        const msg2 = {
          id: this.count,
          event: 101,
          retry: '',
          data: `MESSAGEID####${this.msgId}`,
          docs: this.lastDocs,
          refer_search: this.referSearch,
          search_total: this.searchTotal,
          search_keyword: this.searchKeyword,
          file: this.file,
        }
        this.onmessage?.(msg2);
      }
      let event = 200;
      let content = json?.message?.content;
      
      if (content === '<think>') {
        this.isThink = true;
        this.thinkStartTime = +new Date;
        return;
      }
      if (content === '</think>') {
        this.isThink = false;
        event = 102;
        content = JSON.stringify({
          type: "reasoning_duration",
          message: (((+new Date) - this.thinkStartTime) / 1000).toFixed(1),
        })
        // return;
      }
      if (this.isThink) {
        event = 102;
        content = JSON.stringify({
          type: "reasoning_text",
          message: content,
        })
      }
      const cost = {
        speed: 0
      }
      if (json?.eval_duration > 0 && json?.prompt_eval_duration > 0) {
        cost.speed = Math.round((json.prompt_eval_count + json.eval_count) / ((json.prompt_eval_duration + json.eval_duration) / 1000000000));
      }
      const msg = {
        id: this.count,
        event,
        retry: '',
        data: content,
      }
      if (json?.done) {
        msg.cost = cost;
      }
      this.onmessage?.(msg);
      if (json?.done) {
        this.isDone = true;
        this.onclose();
      }
    } else if (json?.done) {
      if (!this.isDone) {
        this.isDone = true;
        this.onclose();
      }
    } else {
      this.onerror();
    }
  }

  stop() {
    client.run('BlockOllamaQuery', res => {
      // console.log('>>>BlockOllamaQuery', res);
    });
    this.onclose();
  }

  destory() {
    if (!this.isDone) {
      this.stop();
    } else {
      this.onclose();
    }
  }

  whenInitReady (fn = () => {}) {
    if(this.initReady){
      try{
        fn();
      } catch(e){}
    }
    else {
      this.initReadyCallback.push(fn);
    }
  }
  doInitReady () {
    if(this.initReadyCallback.length){
      this.initReadyCallback.forEach(fn => {
        try{
          fn();
        } catch(e){}
      });
      this.initReadyCallback = [];
    }
  }
}

const localRobot = new LocalRobotStore();

window._wsClientCbTags = async (res) => {
  console.log('_wsClientCbTags', res);
  if (res?.data) {
    try {
      const localModelMap = await getLocalModelInfo();
      localRobot.updateModels(res.data, localModelMap);
    } catch (error) {
      localRobot.updateModels(res.data);
    }
  }

  localRobot.initReady = true;
  localRobot.doInitReady();
}

window._wsClientMsg = (res) => {
  console.log('_wsClientMsg', res?.data);
  try {
    let list = [];
    if (res?.data) {
      const data = res.data.split('\n');
      list = data.filter(item => item.length > 0);
    }
    list.forEach(item => {
      localRobot.message(item);
    })
  } catch (err) {}
}

export const RobotsMap = LocalModelConf;
export default localRobot;
